import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  analyzeBusiness,
  analyzeProspect,
  ApiError,
  getQuotaStatus,
  getSectors,
  ignoreBusiness,
  listProspects,
  markBusinessTreated,
  onWakeUp,
  refreshProspect,
  removeProspect,
  saveProspect,
  searchBusinesses,
  setBusinessWebsiteQuality,
  setWebsiteQuality,
  unignoreBusiness,
  untreatBusiness,
  updateProspect,
  WAKE_UP_MESSAGE,
} from '../api/client';
import {
  getCompteCourant,
  onSessionChange,
  signOut,
  type CompteConnecte,
} from '../api/session';
import { buildFileName, downloadCsv } from '../lib/csv';
import { applyFilters, countBy, countByOpenState, sortToTreat } from '../lib/filters';
import type {
  Business,
  OpenFilter,
  Prospect,
  ProspectPatch,
  QuotaStatusResponse,
  ResultFilter,
  SearchFormValues,
  SearchResponse,
  SectorGroup,
  SortKey,
  WebsiteQuality,
} from '../types';
import { CallOutcomeSheet } from './components/CallOutcomeSheet';
import { CupMark, IconExit } from './components/Icons';
import { QuotaPill, TabBar } from './components/TabBar';
import { clearTime, writeTime } from './lib/rdv';
import { AppointmentSheet } from './screens/AppointmentSheet';
import {
  INITIAL_LIBRARY_FILTERS,
  LibraryScreen,
  type LibraryFilters,
  type LibraryPane,
} from './screens/LibraryScreen';
import { LoginScreen } from './screens/LoginScreen';
import { ProspectSheet, type ProspectAction } from './screens/ProspectSheet';
import { ResultsScreen } from './screens/ResultsScreen';
import { SearchForm } from './screens/SearchForm';

export const MIN_RADIUS_KM = 1;
export const MAX_RADIUS_KM = 50;

/** « Ouvert maintenant » doit rester juste sans forcer un rechargement. */
const CLOCK_REFRESH_MS = 60_000;

/** Suivi post-appel : voir l'effet de `visibilitychange` dans `Carnet`. */
const PENDING_CALL_KEY = 'prospect-finder:pending-call';

/**
 * Deux onglets. Les rendez-vous ne sont pas un lieu séparé : ce sont des fiches
 * de la bibliothèque qui ont une date, et ils vivent dans un volet du même
 * écran (voir `screens/LibraryScreen.tsx`).
 */
export type Tab = 'search' | 'library';

/** Avancement d'une analyse groupée : le lot ne part jamais tout seul. */
export interface BatchProgress {
  done: number;
  total: number;
}

const ECO_KEY = 'lpf.ecoMode';

/**
 * Le mode économique est une préférence de consommation, pas un paramètre de
 * recherche : il survit au rechargement. Le remettre à zéro à chaque visite
 * ferait payer des requêtes à quelqu'un qui avait choisi de ne pas les payer.
 *
 * La clé est partagée avec l'autre interface, volontairement : c'est la même
 * personne, le même compte Google, le même argent.
 */
function readEcoMode(): boolean {
  try {
    return window.localStorage.getItem(ECO_KEY) === '1';
  } catch {
    return false;
  }
}

const INITIAL_VALUES: SearchFormValues = {
  address: '',
  query: '',
  radiusKm: 10,
  includeIgnored: false,
  sectorId: '',
  ecoMode: readEcoMode(),
};

/**
 * Racine d'Hermès : elle ne décide que d'une chose, qui est connecté.
 *
 * La session appartient au SDK Supabase, jamais à React : le jeton est
 * renouvelé en tâche de fond, et une copie gardée dans un état local finirait
 * par affirmer le contraire de la vérité. On s'abonne, on n'archive pas.
 */
export function App() {
  // `undefined` = on ne sait pas encore. Distinguer cet état de « déconnecté »
  // évite de faire clignoter l'écran de connexion à chaque rechargement.
  const [compte, setCompte] = useState<CompteConnecte | null | undefined>(undefined);

  useEffect(() => {
    let vivant = true;
    let evenementRecu = false;

    const desabonner = onSessionChange((suivant) => {
      evenementRecu = true;
      if (vivant) setCompte(suivant);
    });

    // Filet de sécurité : le SDK publie normalement un état initial dès
    // l'abonnement. S'il ne le fait pas, l'écran resterait bloqué.
    void getCompteCourant().then((initial) => {
      if (vivant && !evenementRecu) setCompte(initial);
    });

    return () => {
      vivant = false;
      desabonner();
    };
  }, []);

  const handleSignOut = useCallback(() => {
    void signOut();
  }, []);

  if (compte === undefined) {
    return (
      <div className="signin">
        <p className="signin-note" role="status">
          Ouverture de la session…
        </p>
      </div>
    );
  }

  if (compte === null) return <LoginScreen />;

  return <Carnet email={compte.email ?? 'Compte connecté'} onSignOut={handleSignOut} />;
}

interface CarnetProps {
  email: string;
  onSignOut: () => void;
}

function Carnet({ email, onSignOut }: CarnetProps) {
  const [tab, setTab] = useState<Tab>('search');

  /**
   * Recherche et résultats ne cohabitent pas : le formulaire cède la place une
   * fois la recherche lancée, et se retrouve par « Modifier ». Sept contrôles
   * au-dessus de la liste, ce sont sept contrôles à dépasser au défilement à
   * chaque consultation.
   */
  const [showResults, setShowResults] = useState(false);

  const [values, setValues] = useState<SearchFormValues>(INITIAL_VALUES);

  /** Enregistre la préférence dès qu'elle change, sans passer par un effet. */
  const updateValues = useCallback((next: SearchFormValues) => {
    setValues((current) => {
      if (next.ecoMode !== current.ecoMode) {
        try {
          window.localStorage.setItem(ECO_KEY, next.ecoMode ? '1' : '0');
        } catch {
          // Sans stockage, la préférence ne vaut que pour cette visite.
        }
      }
      return next;
    });
  }, []);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<SearchResponse | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [sectorGroups, setSectorGroups] = useState<readonly SectorGroup[]>([]);
  const [quotaStatus, setQuotaStatus] = useState<QuotaStatusResponse | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(true);
  const [quotaError, setQuotaError] = useState(false);

  const [libraryProspects, setLibraryProspects] = useState<Prospect[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [libraryError, setLibraryError] = useState<string | null>(null);

  const [filter, setFilter] = useState<ResultFilter>('all');
  const [openFilter, setOpenFilter] = useState<OpenFilter>('all');
  const [localSearch, setLocalSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('score');

  const [libraryFilters, setLibraryFilters] = useState<LibraryFilters>(INITIAL_LIBRARY_FILTERS);
  const [libraryPane, setLibraryPane] = useState<LibraryPane>('saved');

  const [openProspectId, setOpenProspectId] = useState<number | null>(null);
  const [appointmentFor, setAppointmentFor] = useState<number | null>(null);
  /** Fiche dont une action de liste est en vol : pas de double clic. */
  const [rowBusyId, setRowBusyId] = useState<number | null>(null);
  const [prospectBusy, setProspectBusy] = useState<ProspectAction>(null);
  const [prospectSaving, setProspectSaving] = useState(false);
  const [prospectError, setProspectError] = useState<string | null>(null);

  const [pendingPlaceId, setPendingPlaceId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState<ReadonlySet<string>>(() => new Set());
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null);
  const [waking, setWaking] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [toast, setToast] = useState<string | null>(null);

  /**
   * Repoussés « pour cette fois » après un « Pas de réponse » : purement
   * local, jamais persisté, remis à zéro à chaque nouvelle recherche.
   */
  const [deferredPlaceIds, setDeferredPlaceIds] = useState<ReadonlySet<string>>(() => new Set());
  /**
   * Entreprise dont on attend l'issue d'appel.
   *
   * Relue depuis `sessionStorage` dès le premier rendu : un appel dure assez
   * longtemps pour que Safari ou Chrome mobile déchargent l'onglet en
   * arrière-plan et rejouent la page au retour, ce qui perdrait la question.
   */
  const [callOutcomeFor, setCallOutcomeFor] = useState<Business | null>(() => {
    const raw = sessionStorage.getItem(PENDING_CALL_KEY);
    if (raw === null) return null;
    try {
      return JSON.parse(raw) as Business;
    } catch {
      sessionStorage.removeItem(PENDING_CALL_KEY);
      return null;
    }
  });

  const searchAbort = useRef<AbortController | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  /** Message fugace. Un seul à la fois : le suivant remplace le précédent. */
  const flash = useCallback((message: string) => {
    window.clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = window.setTimeout(() => setToast(null), 2600);
  }, []);

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), CLOCK_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => onWakeUp(setWaking), []);

  /**
   * Ouvre la question **tout de suite**, au clic sur le bouton d'appel.
   *
   * Guetter le retour (`visibilitychange`, `focus`) paraissait plus propre mais
   * ne marche pas : sur iOS un lien `tel:` ouvre une alerte native sans jamais
   * faire passer la page en « masquée », et les événements de retour n'arrivent
   * pas de la même façon d'un navigateur mobile à l'autre. Poser la feuille
   * avant de partir ne dépend d'aucun de ces signaux : le composeur prend
   * l'écran dans la seconde, la question n'est donc pas vue en partant — elle
   * attend simplement au retour.
   *
   * Doublée dans `sessionStorage` pour survivre au rechargement de l'onglet.
   */
  const armCallOutcome = useCallback((business: Business) => {
    sessionStorage.setItem(PENDING_CALL_KEY, JSON.stringify(business));
    setCallOutcomeFor(business);
  }, []);

  /** Referme la question et oublie l'appel en attente. */
  const dismissCallOutcome = useCallback(() => {
    sessionStorage.removeItem(PENDING_CALL_KEY);
    setCallOutcomeFor(null);
  }, []);

  const refreshQuota = useCallback(async (signal?: AbortSignal) => {
    setQuotaLoading(true);
    try {
      setQuotaStatus(await getQuotaStatus(signal));
      setQuotaError(false);
    } catch {
      setQuotaError(true);
    } finally {
      setQuotaLoading(false);
    }
  }, []);

  const refreshLibrary = useCallback(async () => {
    setLibraryLoading(true);
    try {
      setLibraryProspects(await listProspects());
      setLibraryError(null);
    } catch (error) {
      setLibraryError(error instanceof ApiError ? error.message : 'Bibliothèque indisponible.');
    } finally {
      setLibraryLoading(false);
    }
  }, []);

  /**
   * Chargement initial : les trois appels partent ensemble.
   *
   * `Promise.allSettled` évite la cascade — sur un hébergeur qui met jusqu'à
   * une minute à se réveiller, trois requêtes en série coûteraient trois
   * réveils apparents. Et `allSettled` plutôt qu'`all` : un secteur
   * indisponible ne doit pas priver l'écran de ses compteurs, ni l'inverse.
   */
  useEffect(() => {
    let active = true;

    void (async () => {
      const [quota, library, sectors] = await Promise.allSettled([
        getQuotaStatus(),
        listProspects(),
        getSectors(),
      ]);
      if (!active) return;

      if (quota.status === 'fulfilled') {
        setQuotaStatus(quota.value);
        setQuotaError(false);
      } else {
        setQuotaError(true);
      }
      setQuotaLoading(false);

      if (library.status === 'fulfilled') {
        setLibraryProspects(library.value);
        setLibraryError(null);
      } else {
        setLibraryError('Bibliothèque indisponible.');
      }
      setLibraryLoading(false);

      // Sans taxonomie, la saisie libre reste disponible : ce n'est pas une
      // panne bloquante, donc rien ne s'affiche.
      if (sectors.status === 'fulfilled') setSectorGroups(sectors.value.groups);
    })();

    return () => {
      active = false;
    };
  }, []);

  function validate(): boolean {
    const errors: Record<string, string> = {};
    if (values.address.trim() === '') {
      errors.address = 'Renseignez une adresse ou une ville.';
    }
    if (!Number.isFinite(values.radiusKm)) {
      errors.radiusKm = 'Renseignez un rayon.';
    } else if (values.radiusKm < MIN_RADIUS_KM || values.radiusKm > MAX_RADIUS_KM) {
      errors.radiusKm = `Le rayon va de ${MIN_RADIUS_KM} à ${MAX_RADIUS_KM} km.`;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  const runSearch = useCallback(
    async (options: { refresh?: boolean; loadMore?: boolean } = {}) => {
      searchAbort.current?.abort();
      const controller = new AbortController();
      searchAbort.current = controller;

      if (options.loadMore === true) {
        setLoadingMore(true);
      } else {
        setStatus('loading');
        setResult(null);
        setDeferredPlaceIds(new Set());
      }
      setActionError(null);
      setErrorMessage(null);

      try {
        const response = await searchBusinesses({ ...values, ...options }, controller.signal);
        setResult((current) =>
          options.loadMore === true && current !== null
            ? {
                search: response.search,
                count: response.count,
                businesses: [...current.businesses, ...response.businesses].filter(
                  (business, index, all) =>
                    all.findIndex((item) => item.placeId === business.placeId) === index,
                ),
              }
            : response,
        );
        setStatus('success');
        void refreshQuota();
      } catch (error) {
        if (controller.signal.aborted) return;
        setStatus('error');
        setErrorMessage(error instanceof ApiError ? error.message : 'La recherche a échoué.');
      } finally {
        setLoadingMore(false);
      }
    },
    [values, refreshQuota],
  );

  function handleSubmit(): void {
    if (!validate()) return;
    setShowResults(true);
    void runSearch();
  }

  const visible = useMemo(
    () =>
      applyFilters(result?.businesses ?? [], {
        filter,
        openFilter,
        search: localSearch,
        sort,
        now,
      }),
    [result, filter, openFilter, localSearch, sort, now],
  );

  const counts = useMemo(() => countBy(result?.businesses ?? []), [result]);

  const openCounts = useMemo(
    () => countByOpenState(result?.businesses ?? [], now),
    [result, now],
  );

  /**
   * Un « Pas de réponse » ne change aucun état persisté : il repousse juste
   * l'entreprise en fin de liste pour cette recherche-ci. `deferredPlaceIds`
   * est remis à zéro à chaque nouvelle recherche (voir `runSearch`).
   */
  const ordered = useMemo(() => {
    if (deferredPlaceIds.size === 0) return visible;
    const front: Business[] = [];
    const back: Business[] = [];
    for (const business of visible) {
      (deferredPlaceIds.has(business.placeId) ? back : front).push(business);
    }
    return [...front, ...back];
  }, [visible, deferredPlaceIds]);

  const mainList = useMemo(
    () =>
      ordered.filter(
        (business) => business.websiteType !== 'OWN_WEBSITE' && business.libraryStatus !== 'TREATED',
      ),
    [ordered],
  );

  const toTreat = useMemo(
    () => sortToTreat(ordered.filter((business) => business.websiteType === 'OWN_WEBSITE')),
    [ordered],
  );

  /**
   * Un appel refusé range l'entreprise ici plutôt que dans la liste
   * principale — même logique de rangement que « À traiter » pour celles qui
   * ont déjà un site (ces deux dossiers restent mutuellement exclusifs).
   */
  const treated = useMemo(
    () =>
      ordered.filter(
        (business) => business.libraryStatus === 'TREATED' && business.websiteType !== 'OWN_WEBSITE',
      ),
    [ordered],
  );

  const patchBusiness = useCallback((placeId: string, patch: Partial<Business>) => {
    setResult((current) =>
      current === null
        ? current
        : {
            ...current,
            businesses: current.businesses.map((business) =>
              business.placeId === placeId ? { ...business, ...patch } : business,
            ),
          },
    );
  }, []);

  const runAction = useCallback(async (placeId: string, action: () => Promise<void>) => {
    setPendingPlaceId(placeId);
    setActionError(null);
    try {
      await action();
    } catch (error) {
      setActionError(error instanceof ApiError ? error.message : "L'action a échoué.");
    } finally {
      setPendingPlaceId(null);
    }
  }, []);

  const handleSave = useCallback(
    (business: Business) => {
      void runAction(business.placeId, async () => {
        const prospect = await saveProspect(business.placeId, values.sectorId || undefined);
        patchBusiness(business.placeId, {
          libraryStatus: 'SAVED',
          prospectId: prospect.id,
          prospectStatus: prospect.status,
        });
        flash(`${business.name} — gardé en bibliothèque`);
        void refreshLibrary();
      });
    },
    [runAction, patchBusiness, refreshLibrary, values.sectorId, flash],
  );

  const handleIgnore = useCallback(
    (business: Business) => {
      void runAction(business.placeId, async () => {
        await ignoreBusiness(business.placeId);
        if (values.includeIgnored) {
          patchBusiness(business.placeId, { libraryStatus: 'IGNORED' });
        } else {
          setResult((current) =>
            current === null
              ? current
              : {
                  ...current,
                  businesses: current.businesses.filter(
                    (item) => item.placeId !== business.placeId,
                  ),
                  count: current.count - 1,
                },
          );
        }
        flash(`${business.name} — ignoré`);
      });
    },
    [runAction, patchBusiness, values.includeIgnored, flash],
  );

  const handleUnignore = useCallback(
    (business: Business) => {
      void runAction(business.placeId, async () => {
        await unignoreBusiness(business.placeId);
        patchBusiness(business.placeId, { libraryStatus: 'UNSAVED' });
      });
    },
    [runAction, patchBusiness],
  );

  const handleUntreat = useCallback(
    (business: Business) => {
      void runAction(business.placeId, async () => {
        await untreatBusiness(business.placeId);
        patchBusiness(business.placeId, { libraryStatus: 'UNSAVED' });
      });
    },
    [runAction, patchBusiness],
  );

  /* --- Issue d'un appel ---------------------------------------------------
     Trois gestes, déclenchés depuis `CallOutcomeSheet` au retour sur l'appli. */

  /** Réussi : l'entreprise est gardée si besoin, puis le RDV s'ouvre direct. */
  const handleCallSuccess = useCallback(
    (business: Business) => {
      dismissCallOutcome();
      void runAction(business.placeId, async () => {
        if (business.prospectId !== null) {
          setAppointmentFor(business.prospectId);
          return;
        }
        const prospect = await saveProspect(business.placeId, values.sectorId || undefined);
        patchBusiness(business.placeId, {
          libraryStatus: 'SAVED',
          prospectId: prospect.id,
          prospectStatus: prospect.status,
        });
        setLibraryProspects((current) => [...current, prospect]);
        setAppointmentFor(prospect.id);
      });
    },
    [runAction, patchBusiness, values.sectorId, dismissCallOutcome],
  );

  /** Pas de réponse : rien de persisté, juste repoussé en fin de liste. */
  const handleCallNoAnswer = useCallback(
    (business: Business) => {
      dismissCallOutcome();
      setDeferredPlaceIds((current) => new Set(current).add(business.placeId));
    },
    [dismissCallOutcome],
  );

  /** Refusé : direction « déjà traité », qu'il ait été gardé ou non. */
  const handleCallRefused = useCallback(
    (business: Business) => {
      dismissCallOutcome();
      void runAction(business.placeId, async () => {
        if (business.prospectId !== null) {
          await removeProspect(business.prospectId);
          setLibraryProspects((current) =>
            current.filter((prospect) => prospect.id !== business.prospectId),
          );
        } else {
          await markBusinessTreated(business.placeId);
        }
        patchBusiness(business.placeId, {
          libraryStatus: 'TREATED',
          prospectId: null,
          prospectStatus: null,
        });
        flash(`${business.name} — marqué traité`);
      });
    },
    [runAction, patchBusiness, flash, dismissCallOutcome],
  );

  const handleQuality = useCallback(
    (business: Business, quality: WebsiteQuality) => {
      void runAction(business.placeId, async () => {
        await setBusinessWebsiteQuality(business.placeId, quality);
        patchBusiness(business.placeId, { websiteQuality: quality });
      });
    },
    [runAction, patchBusiness],
  );

  /* --- PageSpeed sur une entreprise non enregistrée ---------------------- */

  /** Une analyse = un appel PageSpeed facturé. Jamais déclenchée toute seule. */
  const analyseUnSite = useCallback(
    async (business: Business) => {
      setAnalyzing((current) => new Set(current).add(business.placeId));
      try {
        const pagespeed = await analyzeBusiness(business.placeId);
        patchBusiness(business.placeId, { pagespeed });
      } finally {
        setAnalyzing((current) => {
          const next = new Set(current);
          next.delete(business.placeId);
          return next;
        });
      }
    },
    [patchBusiness],
  );

  const handleAnalyze = useCallback(
    (business: Business) => {
      void (async () => {
        setActionError(null);
        try {
          await analyseUnSite(business);
        } catch (error) {
          setActionError(error instanceof ApiError ? error.message : "L'analyse a échoué.");
        } finally {
          void refreshQuota();
        }
      })();
    },
    [analyseUnSite, refreshQuota],
  );

  /**
   * Analyse groupée du dossier « À traiter ».
   *
   * Trois de front : assez pour ne pas attendre indéfiniment, assez peu pour ne
   * pas assommer PageSpeed ni le backend gratuit. Un quota atteint arrête tout
   * le lot — les suivants échoueraient de la même façon ; toute autre erreur ne
   * concerne que son site et le lot continue.
   */
  const handleAnalyzeAll = useCallback(() => {
    if (batchProgress !== null) return;

    const file = toTreat.filter((business) => business.pagespeed === null);
    if (file.length === 0) return;

    void (async () => {
      setActionError(null);
      setBatchProgress({ done: 0, total: file.length });

      const queue = [...file];
      let done = 0;
      let stop = false;

      const worker = async (): Promise<void> => {
        while (!stop) {
          const business = queue.shift();
          if (business === undefined) return;
          try {
            await analyseUnSite(business);
          } catch (error) {
            if (
              error instanceof ApiError &&
              (error.code === 'MONTHLY_API_QUOTA_REACHED' ||
                error.code === 'PAGESPEED_NOT_CONFIGURED')
            ) {
              stop = true;
              setActionError(error.message);
              return;
            }
          } finally {
            done += 1;
            setBatchProgress({ done, total: file.length });
          }
        }
      };

      await Promise.all(Array.from({ length: 3 }, worker));

      setBatchProgress(null);
      void refreshQuota();
    })();
  }, [batchProgress, toTreat, analyseUnSite, refreshQuota]);

  /**
   * Export CSV.
   *
   * Le nom vient de la recherche qui a produit ces lignes, pas du formulaire :
   * un secteur choisi vide la saisie libre, et le formulaire a pu changer
   * depuis. Sans ça, un export de plombiers s'appellerait « toutes-categories ».
   */
  const handleExport = useCallback(() => {
    if (result === null || visible.length === 0) return;
    downloadCsv(visible, buildFileName(result.search.query, result.search.address));
  }, [result, visible]);

  /* --- La fiche prospect ------------------------------------------------ */

  /** Remplace la fiche en place : la liste reste juste sans second aller-retour. */
  const replaceProspect = useCallback((fresh: Prospect) => {
    setLibraryProspects((current) =>
      current.map((prospect) => (prospect.id === fresh.id ? fresh : prospect)),
    );
  }, []);

  const patchProspect = useCallback(
    (id: number, patch: ProspectPatch, onDone?: () => void) => {
      void (async () => {
        setProspectSaving(true);
        setProspectError(null);
        try {
          replaceProspect(await updateProspect(id, patch));
          onDone?.();
        } catch (error) {
          setProspectError(
            error instanceof ApiError ? error.message : "L'enregistrement a échoué.",
          );
        } finally {
          setProspectSaving(false);
        }
      })();
    },
    [replaceProspect],
  );

  const runProspectAction = useCallback(
    (action: Exclude<ProspectAction, null>, run: (id: number) => Promise<void>) => {
      if (openProspectId === null) return;
      const id = openProspectId;
      void (async () => {
        setProspectBusy(action);
        setProspectError(null);
        try {
          await run(id);
        } catch (error) {
          setProspectError(error instanceof ApiError ? error.message : "L'action a échoué.");
        } finally {
          setProspectBusy(null);
        }
      })();
    },
    [openProspectId],
  );

  const handleProspectQuality = useCallback(
    (quality: WebsiteQuality) => {
      if (openProspectId === null) return;
      const id = openProspectId;
      void (async () => {
        setProspectError(null);
        try {
          replaceProspect(await setWebsiteQuality(id, quality));
        } catch (error) {
          setProspectError(
            error instanceof ApiError ? error.message : "Le verdict n'a pas été enregistré.",
          );
        }
      })();
    },
    [openProspectId, replaceProspect],
  );

  const handleProspectRefresh = useCallback(() => {
    runProspectAction('refresh', async (id) => {
      replaceProspect(await refreshProspect(id));
      void refreshQuota();
    });
  }, [runProspectAction, replaceProspect, refreshQuota]);

  const handleProspectAnalyze = useCallback(() => {
    runProspectAction('analyze', async (id) => {
      replaceProspect(await analyzeProspect(id));
      void refreshQuota();
    });
  }, [runProspectAction, replaceProspect, refreshQuota]);

  const handleProspectRemove = useCallback(() => {
    runProspectAction('remove', async (id) => {
      const placeId = await removeProspect(id);
      setLibraryProspects((current) => current.filter((prospect) => prospect.id !== id));
      // L'établissement n'est pas perdu : il bascule en « déjà traité » et
      // reste visible, reprenable, dans les résultats affichés.
      patchBusiness(placeId, {
        libraryStatus: 'TREATED',
        prospectId: null,
        prospectStatus: null,
      });
      // L'heure locale n'a plus de fiche à laquelle se rattacher.
      clearTime(id);
      setOpenProspectId(null);
      flash('Fiche retirée — l’établissement reste « déjà traité »');
    });
  }, [runProspectAction, patchBusiness, flash]);

  /* --- Le rendez-vous ---------------------------------------------------- */

  /**
   * Fixer ou déplacer un rendez-vous.
   *
   * La date part au serveur (`nextFollowUpAt`, une colonne `DATE`), l'heure
   * reste sur l'appareil : voir l'en-tête de `lib/rdv.ts` pour le pourquoi.
   * L'heure n'est écrite qu'**après** l'aller-retour réussi — sinon un échec
   * réseau laisserait une heure orpheline sur une fiche sans date.
   */
  const handleConfirmAppointment = useCallback(
    (isoDay: string, time: string) => {
      if (appointmentFor === null) return;
      const id = appointmentFor;
      patchProspect(id, { status: 'APPOINTMENT_SET', nextFollowUpAt: isoDay }, () => {
        if (time === '') clearTime(id);
        else writeTime(id, time);
        setAppointmentFor(null);
        flash('Rendez-vous enregistré');
      });
    },
    [appointmentFor, patchProspect, flash],
  );

  /** Annule le rendez-vous : la fiche retombe dans « Enregistrés ». */
  const cancelAppointment = useCallback(
    (id: number) => {
      patchProspect(id, { status: 'TO_CONTACT', nextFollowUpAt: null }, () => {
        clearTime(id);
        flash('Rendez-vous annulé — la fiche retourne dans « Enregistrés »');
      });
    },
    [patchProspect, flash],
  );

  const handleCancelAppointment = useCallback(() => {
    if (openProspectId === null) return;
    cancelAppointment(openProspectId);
  }, [openProspectId, cancelAppointment]);

  /**
   * La corbeille d'une ligne, dont le sens dépend du volet ouvert.
   *
   * Sur « RDV fixés » elle annule le rendez-vous — la fiche n'est pas perdue,
   * elle redevient un prospect à rappeler. Sur « Enregistrés » elle retire la
   * fiche, et l'établissement reste reconnu comme « déjà traité ». Dans les
   * deux cas le geste veut dire « enlève ça d'ici », d'où la même icône.
   *
   * Aucune confirmation : les deux sont réversibles à peu de frais, et une
   * boîte de dialogue à chaque nettoyage coûterait plus que les rares reprises.
   */
  const handleDiscard = useCallback(
    (prospect: Prospect) => {
      if (rowBusyId !== null) return;

      if (prospect.status === 'APPOINTMENT_SET') {
        cancelAppointment(prospect.id);
        return;
      }

      setRowBusyId(prospect.id);
      void (async () => {
        try {
          const placeId = await removeProspect(prospect.id);
          setLibraryProspects((current) =>
            current.filter((item) => item.id !== prospect.id),
          );
          patchBusiness(placeId, {
            libraryStatus: 'TREATED',
            prospectId: null,
            prospectStatus: null,
          });
          clearTime(prospect.id);
          if (openProspectId === prospect.id) setOpenProspectId(null);
          flash(`${prospect.name} — retiré de la bibliothèque`);
        } catch (error) {
          setLibraryError(
            error instanceof ApiError ? error.message : "La fiche n'a pas pu être retirée.",
          );
        } finally {
          setRowBusyId(null);
        }
      })();
    },
    [rowBusyId, cancelAppointment, patchBusiness, openProspectId, flash],
  );

  /* ----------------------------------------------------------------------- */

  const appointments = useMemo(
    () => libraryProspects.filter((prospect) => prospect.status === 'APPOINTMENT_SET'),
    [libraryProspects],
  );

  const library = useMemo(
    () => libraryProspects.filter((prospect) => prospect.status !== 'APPOINTMENT_SET'),
    [libraryProspects],
  );

  const openProspect = useMemo(
    () => libraryProspects.find((prospect) => prospect.id === openProspectId) ?? null,
    [libraryProspects, openProspectId],
  );

  const appointmentProspect = useMemo(
    () => libraryProspects.find((prospect) => prospect.id === appointmentFor) ?? null,
    [libraryProspects, appointmentFor],
  );

  /**
   * Changer d'onglet. Extrait parce que deux instances de `TabBar` s'en
   * servent — celle du haut et celle du bas — et qu'un geste de navigation ne
   * doit exister qu'en un seul endroit.
   */
  const goToTab = (next: Tab) => {
    setTab(next);
    setOpenProspectId(null);
    setAppointmentFor(null);
    if (next === 'library') void refreshLibrary();
  };

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <img className="brand-mark" src="/hermes/logo.png" alt="" width={30} height={30} />
          <span className="brand-text">
            <span className="brand-name">Hermès</span>
            <span className="brand-sub">{email}</span>
          </span>
        </div>

        <TabBar
          place="top"
          tab={tab}
          onTabChange={goToTab}
          libraryCount={library.length + appointments.length}
        />

        <div className="topbar-end">
          <QuotaPill
            status={quotaStatus}
            isLoading={quotaLoading}
            hasError={quotaError}
            onRetry={() => void refreshQuota()}
          />
          <button
            type="button"
            className="btn-round btn-round-line"
            onClick={onSignOut}
            aria-label="Se déconnecter"
            title="Se déconnecter"
          >
            <IconExit size={16} />
          </button>
        </div>
      </header>

      <main className="main">
        {waking ? (
          <p className="notice notice-info" role="status" style={{ marginBottom: 14 }}>
            {WAKE_UP_MESSAGE}
          </p>
        ) : null}

        {tab === 'search' && !showResults ? (
          <SearchForm
            values={values}
            onValuesChange={updateValues}
            fieldErrors={fieldErrors}
            onSubmit={handleSubmit}
            isLoading={status === 'loading'}
            sectorGroups={sectorGroups}
          />
        ) : null}

        {tab === 'search' && showResults ? (
          status === 'loading' ? (
            <div className="empty" role="status">
              <CupMark className="cup-steam" />
              <p className="empty-title">Infusion en cours…</p>
              <p className="empty-text">
                {values.address === '' ? 'Recherche' : `Autour de ${values.address}`} ·{' '}
                {values.radiusKm} km
              </p>
            </div>
          ) : status === 'error' ? (
            <div className="stack">
              <p className="notice notice-error" role="alert">
                {errorMessage ?? 'La recherche a échoué.'}
                <button type="button" className="link" onClick={() => void runSearch()}>
                  Réessayer
                </button>
              </p>
              <button type="button" className="btn btn-soft" onClick={() => setShowResults(false)}>
                Modifier la recherche
              </button>
            </div>
          ) : result !== null ? (
            <ResultsScreen
              result={result}
              mainList={mainList}
              toTreat={toTreat}
              treated={treated}
              counts={counts}
              openCounts={openCounts}
              filter={filter}
              onFilterChange={setFilter}
              openFilter={openFilter}
              onOpenFilterChange={setOpenFilter}
              localSearch={localSearch}
              onLocalSearchChange={setLocalSearch}
              sort={sort}
              onSortChange={setSort}
              onSave={handleSave}
              onIgnore={handleIgnore}
              onUnignore={handleUnignore}
              onUntreat={handleUntreat}
              onCallStart={armCallOutcome}
              onQuality={handleQuality}
              onAnalyze={handleAnalyze}
              onAnalyzeAll={handleAnalyzeAll}
              analyzing={analyzing}
              batchProgress={batchProgress}
              onExport={handleExport}
              pendingPlaceId={pendingPlaceId}
              actionError={actionError}
              loadingMore={loadingMore}
              onLoadMore={() => void runSearch({ loadMore: true })}
              onRefreshSearch={() => void runSearch({ refresh: true })}
              onEdit={() => setShowResults(false)}
              now={now}
            />
          ) : null
        ) : null}

        {tab === 'library' ? (
          <LibraryScreen
            pane={libraryPane}
            onPaneChange={setLibraryPane}
            saved={library}
            appointments={appointments}
            isLoading={libraryLoading}
            error={libraryError}
            filters={libraryFilters}
            onFiltersChange={setLibraryFilters}
            onOpen={setOpenProspectId}
            onRetry={() => void refreshLibrary()}
            onChanged={() => void refreshLibrary()}
            onDiscard={handleDiscard}
            busyProspectId={rowBusyId}
            sectorGroups={sectorGroups}
            now={now}
          />
        ) : null}
      </main>

      {/* Frère de <main> et non enfant du <header> : elle n'a plus besoin de
          s'échapper de quoi que ce soit par `position: fixed`. */}
      <TabBar
        place="bottom"
        tab={tab}
        onTabChange={goToTab}
        libraryCount={library.length + appointments.length}
      />

      {openProspect !== null ? (
        <ProspectSheet
          key={openProspect.id}
          prospect={openProspect}
          onQualityChange={handleProspectQuality}
          onRefresh={handleProspectRefresh}
          onAnalyze={handleProspectAnalyze}
          onRemove={handleProspectRemove}
          onClose={() => setOpenProspectId(null)}
          onOpenAppointment={() => setAppointmentFor(openProspect.id)}
          onCancelAppointment={handleCancelAppointment}
          busyAction={prospectBusy}
          isSaving={prospectSaving}
          error={prospectError}
          sectorGroups={sectorGroups}
          now={now}
        />
      ) : null}

      {/* Par-dessus la fiche, volontairement : on revient à la fiche en
          fermant, et on y voit le rendez-vous qu'on vient de poser. */}
      {appointmentProspect !== null ? (
        <AppointmentSheet
          key={appointmentProspect.id}
          prospect={appointmentProspect}
          onConfirm={handleConfirmAppointment}
          onClose={() => setAppointmentFor(null)}
          isSaving={prospectSaving}
          now={now}
        />
      ) : null}

      {callOutcomeFor !== null ? (
        <CallOutcomeSheet
          key={callOutcomeFor.placeId}
          business={callOutcomeFor}
          onSuccess={() => handleCallSuccess(callOutcomeFor)}
          onNoAnswer={() => handleCallNoAnswer(callOutcomeFor)}
          onRefused={() => handleCallRefused(callOutcomeFor)}
          onClose={dismissCallOutcome}
          busy={pendingPlaceId === callOutcomeFor.placeId}
        />
      ) : null}

      {toast !== null ? (
        <p className="toast" role="status">
          {toast}
        </p>
      ) : null}
    </div>
  );
}
